import React, { useEffect, useState } from "react";
import { Send, Swords, ThumbsUp, Trophy } from "lucide-react";
import { api, streamFetch } from "../lib/api";
import { readSse } from "../lib/sse";
import { logError } from "../lib/log";
import { useAiModels, PROVIDER_LABEL } from "./ModelPicker";

const SIDE_STYLE = {
  A: { border: "border-fuchsia-400/40", tag: "text-fuchsia-300", ring: "focus:border-fuchsia-400/60" },
  B: { border: "border-emerald-400/40", tag: "text-emerald-300", ring: "focus:border-emerald-400/60" },
};

function ModelSelect({ side, value, onChange, models, disabled }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} data-testid={`faceoff-model-${side}`}
      className={`w-full rounded-md border border-white/10 bg-black/60 px-3 py-2 text-sm text-slate-200 focus:outline-none ${SIDE_STYLE[side].ring}`}>
      {models.map((m) => <option key={m.key} value={m.key}>{PROVIDER_LABEL[m.provider] || m.provider} · {m.label}</option>)}
    </select>
  );
}

function AnswerPane({ side, model, models, text, busy, voted, canVote, onVote, onModel }) {
  const label = models.find((m) => m.key === model)?.label || model;
  const st = SIDE_STYLE[side];
  return (
    <div className={`flex min-w-0 flex-col rounded-md border ${voted === side ? "border-lime-400/60" : st.border} bg-black/40`} data-testid={`faceoff-pane-${side}`}>
      <div className="border-b border-white/10 p-3">
        <div className={`mb-1 text-[10px] uppercase tracking-widest ${st.tag}`}>Side {side}</div>
        <ModelSelect side={side} value={model} onChange={onModel} models={models} disabled={busy} />
      </div>
      <div className="min-h-[160px] flex-1 whitespace-pre-wrap p-4 text-sm leading-relaxed text-slate-100" data-testid={`faceoff-answer-${side}`}>
        {text || (busy ? <span className="font-mono-num text-slate-500">▍ {label} is thinking…</span> : <span className="text-slate-500">Answer from {label} appears here.</span>)}
      </div>
      {canVote && (
        <div className="border-t border-white/10 p-3">
          <button onClick={() => onVote(side)} disabled={!!voted} data-testid={`faceoff-vote-${side}`}
            className={`inline-flex w-full items-center justify-center gap-2 rounded-md border px-3 py-2 text-xs font-semibold ${voted === side ? "border-lime-400/60 bg-lime-500/15 text-lime-200" : "border-white/15 bg-white/5 text-slate-200 hover:bg-white/10"} disabled:cursor-default`}>
            <ThumbsUp size={12} /> {voted === side ? `${label} wins this round` : voted ? "—" : `${label} answered better`}
          </button>
        </div>
      )}
    </div>
  );
}

function Tally({ tally }) {
  if (!tally || tally.total === 0) return null;
  const top = tally.wins[0];
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400" data-testid="faceoff-tally">
      <Trophy size={12} className="text-amber-300" />
      <span>Community favourite: <span className="font-semibold text-amber-200">{top.label}</span></span>
      <span className="text-slate-600">·</span>
      {tally.wins.filter((w) => w.wins > 0).map((w) => <span key={w.key} className="font-mono-num">{w.label} {w.wins}</span>)}
      <span className="text-slate-600">· {tally.total} votes</span>
    </div>
  );
}

async function streamAnswer(question, model, onText) {
  const resp = await streamFetch("/tutor/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ message: question, model, session_id: `faceoff-${crypto.randomUUID()}` }),
  });
  if (!resp.ok || !resp.body) throw new Error(`Tutor request failed (${resp.status})`);
  let acc = "";
  await readSse(resp, (chunk) => { acc += chunk; onText(acc); }, { json: false });
}

export function FaceOff() {
  const models = useAiModels();
  const [a, setA] = useState("sonnet-5");
  const [b, setB] = useState("gpt-5-4");
  const [question, setQuestion] = useState("");
  const [asked, setAsked] = useState("");
  const [ans, setAns] = useState({ A: "", B: "" });
  const [busy, setBusy] = useState({ A: false, B: false });
  const [voted, setVoted] = useState(null);
  const [tally, setTally] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => { api.get("/ai/faceoff/tally").then((r) => setTally(r.data)).catch((e) => logError("Tally failed", e)); }, []);

  const anyBusy = busy.A || busy.B;
  const run = async () => {
    const q = question.trim();
    if (!q || anyBusy) return;
    if (a === b) return setErr("Pick two different models to compare.");
    setErr(null); setAsked(q); setQuestion(""); setVoted(null);
    setAns({ A: "", B: "" });
    setBusy({ A: true, B: true });
    const one = (side, model) => streamAnswer(q, model, (t) => setAns((s) => ({ ...s, [side]: t })))
      .catch((e) => { logError(`Face-off ${side} failed`, e); setAns((s) => ({ ...s, [side]: "Sorry — this model couldn't answer right now." })); })
      .finally(() => setBusy((s) => ({ ...s, [side]: false })));
    await Promise.all([one("A", a), one("B", b)]);
  };

  const vote = async (side) => {
    const winner = side === "A" ? a : b;
    const loser = side === "A" ? b : a;
    setVoted(side);
    try {
      const { data } = await api.post("/ai/faceoff/vote", { question: asked, winner, loser });
      setTally(data);
    } catch (e) {
      logError("Vote failed", e);
    }
  };

  const canVote = !!asked && !anyBusy && !!ans.A && !!ans.B;

  return (
    <div className="space-y-4" data-testid="faceoff-root">
      <div className="rounded-md border border-white/10 bg-black/40 p-3 sm:p-4">
        <div className="flex gap-2">
          <input value={question} onChange={(e) => setQuestion(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} disabled={anyBusy}
            data-testid="faceoff-input" placeholder="Ask one question — both models answer at once…"
            className="min-w-0 flex-1 rounded-md border border-white/10 bg-black/60 px-3 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-amber-400/60 focus:outline-none focus:ring-2 focus:ring-amber-400/30 sm:px-4" />
          <button onClick={run} disabled={anyBusy || !question.trim()} data-testid="faceoff-send-btn"
            className="inline-flex shrink-0 items-center gap-2 rounded-md bg-amber-400 px-4 py-3 text-sm font-semibold text-black transition-colors hover:bg-amber-300 disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-slate-500">
            <Swords size={14} /> <span className="hidden sm:inline">Compare</span><Send size={14} className="sm:hidden" />
          </button>
        </div>
        {err && <div className="mt-2 rounded-md border border-red-400/40 bg-red-500/10 p-2 text-xs text-red-200" data-testid="faceoff-error">{err}</div>}
        {asked && <div className="mt-3 rounded-md border border-cyan-400/30 bg-cyan-500/10 px-3 py-2 text-sm text-cyan-100" data-testid="faceoff-question">{asked}</div>}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <AnswerPane side="A" model={a} models={models} text={ans.A} busy={busy.A} voted={voted} canVote={canVote} onVote={vote} onModel={setA} />
        <AnswerPane side="B" model={b} models={models} text={ans.B} busy={busy.B} voted={voted} canVote={canVote} onVote={vote} onModel={setB} />
      </div>

      <Tally tally={tally} />
    </div>
  );
}
